import React from 'react';
import PropTypes from 'prop-types';

import { downloadReasonsEnabled } from '../../../helpers/Download';
import { userHasFeature } from '../../../helpers/Features';
import { getSettingMapInfo } from '../../../helpers/SettingsMap';

import AdminExport from '../../../containers/Admin/Export';
import AdminFileStatuses from '../../../containers/Admin/FileStatuses';
import AdminUsersContent from './ContentSections/UsersContent';
import AdminRolesContent from './ContentSections/RolesContent';
import AdminTeamsContent from './ContentSections/TeamsContent';
import AdminTaxonomiesContent from './ContentSections/TaxonomiesContent';
import AdminCustomFieldsContent from './ContentSections/CustomFieldsContent';
import SettingsImageEditor from './ContentSections/ImageEditor/ImageEditor';
import SettingsShare from './ContentSections/Share';
import SettingsDownloading from './ContentSections/Downloading';
import SettingsBranding from './ContentSections/Workspace/Branding';
import SettingsWatermark from './ContentSections/Workspace/Watermark';
import SettingsBasicSection from './ContentSections/BasicSection';
import SettingsMetadataContent from './ContentSections/Advanced/Metadata/MetadataContent';
import SettingsMetadataMappingContent from './ContentSections/Advanced/Metadata/MetadataMappingContent';
import Integrations from '../../../containers/Integrations';
import OwnerDetails from './ContentSections/OwnerDetails';
import AdminPayment from '../../../containers/Admin/Payment';
import AdminFilters from '../../../containers/Admin/Filters';
import SettingsPrefixSuffix from './ContentSections/PrefixSuffix';
import Loading from '../../Loading';
import UpgradeBoard from '../../UpgradeBoard';

const SettingsContent = (props) => {
  const { activeTab, loading } = props;

  if (loading) {
    return (
      <div className="settings-inner">
        <div className="settings-inner-content-wrapper">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-inner">
      <div className="settings-inner-content-wrapper">
        {activeTab === 'branding' && (
          userHasFeature('branding') ? (
            <SettingsBranding {...props} />
          ) : (
            <UpgradeBoard feature="branding" />
          )
        )}
        {activeTab === 'watermark' && (
          userHasFeature('watermarking') ? (
            <SettingsWatermark {...props} />
          ) : (
            <UpgradeBoard feature="watermarking" />
          )
        )}
        {activeTab === 'date-and-time' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={['custom_collection_date', 'relative_date_time', 'date_format', 'time_format']}
            title={getSettingMapInfo('date-and-time').label}
          />
        )}
        {activeTab === 'filters' && (
          <AdminFilters />
        )}
        {activeTab === 'dashboard' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={['dashboard_messages', 'home_page']}
            title={getSettingMapInfo('dashboard').label}
          />
        )}
        {activeTab === 'sidebar' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={['sidebar_hide_people', 'hide_collapsable_sidebar']}
            title={getSettingMapInfo('sidebar').title || getSettingMapInfo('sidebar').label}
            description={getSettingMapInfo('sidebar').description}
          />
        )}

        {activeTab === 'ai-tagging' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={['rekognition_taxonomy', 'rekognition_language', 'rekognition_videos', 'face_recognition']}
            title={getSettingMapInfo('ai-tagging').label}
            trialNoticeFeature="face-recognition"
          />
        )}
        {activeTab === 'visibility' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={[
              'default_file_access_type',
              'default_collection_access_type',
              'default_files_view',
              'default_collections_view',
              'default_sort',
              'public_collections_open',
              'hide_collection_file_count',
              'hide_related_files',
            ]}
            title={getSettingMapInfo('visibility').label}
          />
        )}
        {activeTab === 'file-dates-and-expiry' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={[
              'expiration_date',
              'enable_created_date_field',
              'expiration_date_required',
              'expiration_date_action',
            ]}
            title={getSettingMapInfo('file-dates-and-expiry').label}
            trialNoticeFeature="expiration-date"
          />
        )}
        {activeTab === 'file-statuses' && (userHasFeature('file-status') ? (
          <AdminFileStatuses
            {...props}
            viewAllowedSettings={['file_status_required']}
          />
        ) : <UpgradeBoard feature="file-status" />)}
        {activeTab === 'image-editor' && (
          userHasFeature('image-editor') ? (
            <SettingsImageEditor {...props} />
          ) : (
            <UpgradeBoard feature="image-editor" />
          )
        )}
        {(activeTab === 'categories') && <AdminTaxonomiesContent {...props} />}
        {(activeTab === 'custom-fields') && <AdminCustomFieldsContent {...props} />}
        {activeTab === 'file-commenting-and-favoriting' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={['show_comments_for', 'show_likes_for']}
            title={getSettingMapInfo('file-commenting-and-favoriting').label}
          />
        )}
        {activeTab === 'downloading' && (
          <SettingsDownloading
            {...props}
            viewAllowedSettings={[
              'download_original_names',
              ...(downloadReasonsEnabled() ? [
                'download_with_reasons', 'download_with_reasons_admins',
              ] : []),
            ]}
            title={getSettingMapInfo('downloading').label}
          />
        )}
        {activeTab === 'full-screen-image-zoom' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={['allow_zoom']}
            title={getSettingMapInfo('full-screen-image-zoom').label}
          />
        )}
        {activeTab === 'public-urls' && (
          userHasFeature('public-file-urls') ? (
            <SettingsBasicSection
              {...props}
              viewAllowedSettings={['public_file_urls', 'public_file_urls_domain']}
              title={getSettingMapInfo('public-urls').label}
              trialNoticeFeature="public-file-urls"
            />
          ) : (
            <UpgradeBoard feature="public-file-urls" />
          )
        )}
        {activeTab === 'prefix-and-suffix' && (
          userHasFeature('file-name-prefix-suffix') ? (
            <SettingsPrefixSuffix
              {...props}
              viewAllowedSettings={['file_name_prefix_and_suffix_force_workspace']}
              title={getSettingMapInfo('prefix-and-suffix').label}
            />
          ) : (
            <UpgradeBoard feature="file-name-prefix-suffix" />
          )
        )}
        {activeTab === 'search' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={['search_order', 'search_document_content_documents', 'search_document_content_images', 'search_document_content_videos', 'search_document_content_audio']}
            title={getSettingMapInfo('search').label}
            trialNoticeFeature="search-document-content"
          />
        )}
        {activeTab === 'export' && (
          userHasFeature('export-metadata') ? (
            <AdminExport {...props} />
          ) : (
            <UpgradeBoard feature="export-metadata" />
          )
        )}
        {activeTab === 'custom-pages' && (
          userHasFeature('custom-pages') ? (
            <SettingsBasicSection
              {...props}
              viewAllowedSettings={['hide_stockpress_branding_custom_pages']}
              title={getSettingMapInfo('custom-pages').label}
              trialNoticeFeature="custom-pages"
            />
          ) : (
            <UpgradeBoard feature="custom-pages" />
          )
        )}

        {(activeTab === 'users') && <AdminUsersContent {...props} />}
        {(activeTab === 'roles') && <AdminRolesContent {...props} />}
        {(activeTab === 'teams') && <AdminTeamsContent {...props} />}

        {activeTab === '2fa' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={['2fa_required']}
            title={getSettingMapInfo('2fa').label}
          />
        )}

        {activeTab === 'google-login' && (
          <SettingsBasicSection
            {...props}
            viewAllowedSettings={['google_login_enabled', 'google_login_position', 'google_login_allowed_domains', 'google_login_create_accounts', 'google_login_default_role', 'google_login_default_teams']}
            title={getSettingMapInfo('google-login').label}
          />
        )}

        {activeTab === 'share-settings' && (
          (userHasFeature('share-templates') || userHasFeature('hide-stockpress-branding-shares')) ? (
            <SettingsShare
              {...props}
            />
          ) : (
            <UpgradeBoard feature="share-templates" />
          )
        )}

        {activeTab === 'integrations' && (
          <Integrations
            {...props}
          />
        )}

        {activeTab === 'ftp' && (
          userHasFeature('ftp') ? (
            <SettingsBasicSection
              {...props}
              viewAllowedSettings={['enable_ftp_importer']}
              title={getSettingMapInfo('ftp').label}
              trialNoticeFeature="ftp"
            />
          ) : (
            <UpgradeBoard feature="ftp" />
          )
        )}
        {activeTab === 'iptc-and-exif-mapping' && (
          userHasFeature('metadata-mapping') ? (
            <SettingsMetadataContent
              {...props}
              viewAllowedSettings={['exif_mapping_enabled', 'use_exif_mapping_by_default']}
              title={getSettingMapInfo('iptc-and-exif-mapping').label}
            />
          ) : (
            <UpgradeBoard feature="metadata-mapping" />
          )
        )}
        {activeTab === 'sync-metadata' && (
          userHasFeature('download-file-with-metadata') ? (
            <SettingsMetadataMappingContent
              {...props}
              viewAllowedSettings={['download_file_with_metadata']}
              title={getSettingMapInfo('sync-metadata').label}
            />
          ) : (
            <UpgradeBoard feature="download-file-with-metadata" />
          )
        )}

        {activeTab === 'owner-details' && (
          <OwnerDetails
            {...props}
          />
        )}
        {activeTab === 'subscription' && (
          <AdminPayment
            {...props}
          />
        )}
      </div>
    </div>
  );
};

SettingsContent.propTypes = {
  activeTab: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default SettingsContent;
